import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

const WishlistContext = createContext(null);
const STORAGE_KEY = "wishlist_v1";

function loadWishlist() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const arr = raw ? JSON.parse(raw) : [];
        return Array.isArray(arr) ? arr : [];
    } catch (_) {
        return [];
    }
}

function saveWishlist(items) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (_) {
        /* ignore */
    }
}

export function WishlistProvider({ children }) {
    const [items, setItems] = useState(loadWishlist);

    useEffect(() => {
        saveWishlist(items);
    }, [items]);

    const has = useCallback(
        (id) => items.some((p) => String(p.id) === String(id)),
        [items],
    );

    const add = useCallback((product) => {
        if (!product || product.id == null) return;
        setItems((prev) =>
            prev.some((p) => String(p.id) === String(product.id))
                ? prev
                : [
                    ...prev,
                    {
                        id: product.id,
                        name: product.name,
                        image: product.image,
                        price: Number(product.price) || 0,
                        oldPrice: product.oldPrice ?? null,
                        categoryId: product.categoryId ?? null,
                    },
                ],
        );
    }, []);

    const remove = useCallback((id) => {
        setItems((prev) => prev.filter((p) => String(p.id) !== String(id)));
    }, []);

    const toggle = useCallback(
        (product) => {
            if (!product || product.id == null) return;
            if (has(product.id)) remove(product.id);
            else add(product);
        },
        [add, remove, has],
    );

    const clear = useCallback(() => setItems([]), []);

    const value = useMemo(
        () => ({ items, count: items.length, has, add, remove, toggle, clear }),
        [items, has, add, remove, toggle, clear],
    );

    return (
        <WishlistContext.Provider value={value}>
            {children}
        </WishlistContext.Provider>
    );
}

export function useWishlist() {
    const ctx = useContext(WishlistContext);
    if (!ctx) throw new Error("useWishlist must be used within WishlistProvider");
    return ctx;
}
